/**
 * URL ⇄ query-state codec for the MCAP log list.
 *
 * The URL is the single source of truth for filters, pagination and sort, so
 * a results view can be shared by link and survives a reload. Filter keys
 * mirror the raasdata-service query parameters (snake_case; `tasks` and
 * `scene_types` repeat, `?tasks=pick&tasks=place`), the page's own pagination/sort keys are
 * camelCase. Malformed values fall back to defaults instead of reaching the
 * backend, which answers unknown enum values and sort keys with a 422.
 */
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  PAGE_SIZE_OPTIONS,
  PROCESSING_STATE_OPTIONS,
  RECORDING_STATE_OPTIONS,
  SORT_FIELDS,
  UPLOAD_STATE_OPTIONS,
} from "./logConstants";

const UPLOAD_STATES = UPLOAD_STATE_OPTIONS.map((option) => option.value);
const PROCESSING_STATES = PROCESSING_STATE_OPTIONS.map((option) => option.value);
const RECORDING_STATES = RECORDING_STATE_OPTIONS.map((option) => option.value);

/** Distinct, trimmed values of a repeated parameter (`?tasks=a&tasks=b`). */
const readNames = (params, key) => [
  ...new Set(params.getAll(key).map((name) => name.trim()).filter(Boolean)),
];

/**
 * Inclusive time-window bounds. `uploaded_*` bound the upload time,
 * `recorded_*` the recording START time; each travels as an ISO-8601 UTC
 * instant ("2026-02-25T15:00:00Z"), which both the URL and the API accept.
 */
export const TIME_WINDOW_KEYS = [
  "uploaded_from",
  "uploaded_to",
  "recorded_from",
  "recorded_to",
];

/**
 * Canonical URL/API form of an instant: ISO-8601 UTC at second precision.
 * Accepts a Date, epoch milliseconds, a date string, or anything whose
 * `valueOf()` is epoch milliseconds (a dayjs value); unparseable input yields
 * undefined so it drops out of the query instead of producing a 422.
 */
export const toQueryInstant = (value) => {
  if (value == null || value === "") return undefined;
  const ms = typeof value === "string" ? Date.parse(value) : Number(value);
  if (!Number.isFinite(ms)) return undefined;
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
};

/**
 * Parse filter/pagination/sort state from the URL search string.
 */
export const parseQuery = (search) => {
  const params = new URLSearchParams(search);
  const page = parseInt(params.get("page"), 10);
  const pageSize = parseInt(params.get("pageSize"), 10);
  const uploadState = params.get("upload_state");
  const processingState = params.get("processing_state");
  const recordingState = params.get("recording_state");
  const sortBy = params.get("sortBy");
  const sortOrder = params.get("sortOrder");
  // Blank and repeated names are noise, not extra filters.
  const tasks = readNames(params, "tasks");
  const sceneTypes = readNames(params, "scene_types");

  const query = {
    robot_id: params.get("robot_id") || undefined,
    location: params.get("location") || undefined,
    // Whitelisted like sortBy: the backend rejects unknown states with a 422.
    upload_state: UPLOAD_STATES.includes(uploadState) ? uploadState : undefined,
    processing_state: PROCESSING_STATES.includes(processingState)
      ? processingState
      : undefined,
    recording_state: RECORDING_STATES.includes(recordingState)
      ? recordingState
      : undefined,
    tasks: tasks.length > 0 ? tasks : undefined,
    scene_types: sceneTypes.length > 0 ? sceneTypes : undefined,
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    pageSize: PAGE_SIZE_OPTIONS.includes(pageSize) ? pageSize : DEFAULT_PAGE_SIZE,
    // Whitelisted: the backend rejects unknown sort fields with a 422, so a
    // hand-edited URL must never reach it.
    sortBy: SORT_FIELDS.includes(sortBy) ? sortBy : DEFAULT_SORT_BY,
    sortOrder:
      sortOrder === "ascend" || sortOrder === "descend"
        ? sortOrder
        : DEFAULT_SORT_ORDER,
  };
  TIME_WINDOW_KEYS.forEach((key) => {
    query[key] = toQueryInstant(params.get(key));
  });
  return query;
};

/**
 * Serialize a query object back into a URL search string, omitting defaults
 * so shared URLs stay clean.
 */
export const buildSearch = (query) => {
  const params = new URLSearchParams();
  if (query.robot_id) params.set("robot_id", query.robot_id);
  if (query.location) params.set("location", query.location);
  if (query.upload_state) params.set("upload_state", query.upload_state);
  if (query.processing_state) params.set("processing_state", query.processing_state);
  if (query.recording_state) params.set("recording_state", query.recording_state);
  (query.tasks || []).forEach((task) => params.append("tasks", task));
  (query.scene_types || []).forEach((scene) => params.append("scene_types", scene));
  TIME_WINDOW_KEYS.forEach((key) => {
    if (query[key]) params.set(key, query[key]);
  });
  // Pagination and sort keys are absent from a link built from filters alone
  // (the dashboard's chart items) and then mean "default".
  if (query.page > 1) params.set("page", query.page);
  if (query.pageSize !== undefined && query.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set("pageSize", query.pageSize);
  }
  if (query.sortBy !== undefined && query.sortBy !== DEFAULT_SORT_BY) {
    params.set("sortBy", query.sortBy);
  }
  if (query.sortOrder !== undefined && query.sortOrder !== DEFAULT_SORT_ORDER) {
    params.set("sortOrder", query.sortOrder);
  }
  const search = params.toString();
  return search ? `?${search}` : "";
};

/**
 * The dashboard's statistics window: which timeline it bounds (`uploaded`
 * = upload time, `recorded` = recording start) and the bounds themselves,
 * read from the same URL keys the list page uses. Upload bounds win when
 * both kinds are present; no bounds at all means all time.
 */
export const parseWindowQuery = (search) => {
  const params = new URLSearchParams(search);
  const read = (key) => toQueryInstant(params.get(key));
  const uploaded = { from: read("uploaded_from"), to: read("uploaded_to") };
  const recorded = { from: read("recorded_from"), to: read("recorded_to") };
  const field =
    !uploaded.from && !uploaded.to && (recorded.from || recorded.to) ? "recorded" : "uploaded";
  const bounds = field === "recorded" ? recorded : uploaded;
  return { field, from: bounds.from, to: bounds.to };
};

/** A window as list/API filter keys (`uploaded_from`… or `recorded_from`…). */
export const windowFilters = ({ field, from, to }) => ({
  [`${field}_from`]: from,
  [`${field}_to`]: to,
});

/** AntD's sort vocabulary ("ascend"/"descend") → the API's ("asc"/"desc"). */
export const toApiSortOrder = (sortOrder) => (sortOrder === "ascend" ? "asc" : "desc");
